import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioButtonChecked, RadioButtonUnchecked } from '@material-ui/icons';
import { Accordion, BaseClickable, Box, palette, Stack, Text } from '@solvhealth/jigsaw';
import { InsuranceProfile } from '~/components/Paperwork/hooks/useInsuranceProfiles';

type Props = {
  profile: InsuranceProfile;
  handleInsuranceProfileSelect: (insurance_profile_id?: string) => void;
  isSelected: boolean;
  manualInsuranceInputs: ReactNode;
};

const InsuranceProfileCard = (props: Props) => {
  const { t } = useTranslation(['paperwork', 'common']);
  const { profile, handleInsuranceProfileSelect, isSelected, manualInsuranceInputs } = props;
  const borderColor = isSelected ? 'gray-600' : 'gray-200';
  const border = isSelected ? 'solid 2px' : 'solid 1px';
  const radio = isSelected ? (
    <RadioButtonChecked style={{ color: `${palette.gray['600']}` }} />
  ) : (
    <RadioButtonUnchecked style={{ color: `${palette.gray['400']}` }} />
  );
  const defaultValue = 'not on file';

  const getFormFields = () => {
    return (
      <Box
        sx={{
          '> :not(:last-child)': {
            marginBottom: 3,
          },
        }}
      >
        {manualInsuranceInputs}
      </Box>
    );
  };

  return (
    <Stack border={border} borderColor={borderColor} borderRadius="base" width="100%">
      <BaseClickable
        endIcon={<Box paddingRight={2}>{radio}</Box>}
        key={profile.insurance_profile_id}
        onClick={() => handleInsuranceProfileSelect(profile.insurance_profile_id)}
        p={3}
        type="button"
      >
        <Stack space={1} width="100%">
          <Text mb={2} variant="subtitle">
            {profile.insurer_name}
          </Text>
          <Stack justifyContent="space-between" row>
            <Text color="gray-500" flex={1} variant="body2">
              Plan type
            </Text>
            <Text flex={1} variant="body2">
              {profile.insurer_type}
            </Text>
          </Stack>
          <Stack justifyContent="space-between" row>
            <Text color="gray-500" flex={1} variant="body2">
              Member name
            </Text>
            <Text flex={1} variant="body2">
              {profile.first_name} {profile.last_name}
            </Text>
          </Stack>
          <Stack justifyContent="space-between" row>
            <Text color="gray-500" flex={1} variant="body2">
              Member ID
            </Text>
            <Text flex={1} variant="body2">
              {profile.member_code || defaultValue}
            </Text>
          </Stack>
          <Stack justifyContent="space-between" row>
            <Text color="gray-500" flex={1} variant="body2">
              Group ID
            </Text>
            <Text flex={1} variant="body2">
              {profile.group_number || defaultValue}
            </Text>
          </Stack>
        </Stack>
      </BaseClickable>
      {isSelected && (
        <>
          <hr
            style={{
              border: `1px solid ${palette.gray['200']}`,
              margin: '0 24px',
            }}
          />
          <Box
            sx={{
              '.MuiAccordion-root': {
                border: '0 !important',
              },
            }}
          >
            <Accordion
              accordionVariant="card"
              body={getFormFields()}
              expanded
              title={t(`paperwork:payment.confirmYourInsurance`)}
            />
          </Box>
        </>
      )}
    </Stack>
  );
};

export default InsuranceProfileCard;
